import {Overlay} from "@angular/cdk/overlay";
import {APP_BASE_HREF} from "@angular/common";
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr} from "@angular/common/http";
import {ErrorHandler, inject, NgModule, provideAppInitializer, provideZoneChangeDetection} from "@angular/core";
import {MAT_MENU_SCROLL_STRATEGY} from "@angular/material/menu";
import {BrowserModule} from "@angular/platform-browser";
import {provideAnimations} from "@angular/platform-browser/animations";

import {AppContext, AppEnvironmentConfiguration, AppErrorService, AppReportConfiguration, AppService} from "app/app.service";
import {RootRoutingModule} from "app/root-routing.module";
import {RootComponent} from "app/root.component";
import {AuthGuard} from "app/services/domain/auth.guard";
import {DomainModule} from "app/services/domain/domain.module";
import * as Models from "app/services/proxy/model/models";
import {FeatureFlagsRequester} from "app/services/third-party/feature-flags/feature-flags-requester.service";
import {FeatureFlagsService} from "app/services/third-party/feature-flags/feature-flags.service";
import {environment} from "environments/environment";
import {FrameworkServicesModule} from "framework/services";
import {ApiClientConfiguration} from "framework/services/api.client";
import {DedupeInterceptor} from "framework/services/dedupe-interceptor/dedupe.interceptor";
import {FRAMEWORK_FEATURE_FLAGS, FrameworkFeatureFlag, frameworkFeatureFlagsContextFactory} from "framework/services/framework-feature-flags.service";
import {SourceTypeInterceptor} from "framework/services/source-type-interceptor/source-type.interceptor";
import {TraceHeadersInterceptor} from "framework/services/trace-headers-interceptor/trace-headers.interceptor";
import {NgxPendoModule} from "ngx-pendo";

@NgModule({
              declarations: [
                  RootComponent
              ],
              exports     : [],
              bootstrap   : [RootComponent],
              imports     : [
                  // Core
                  BrowserModule,
                  DomainModule,
                  FrameworkServicesModule,
                  // Routing
                  RootRoutingModule,
                  // Pendo
                  NgxPendoModule.forRoot({pendoApiKey: "2030bc0a-b2fb-4f1f-696b-f3d42756931f"})
              ],
              providers   : [
                  AuthGuard,
                  {
                      provide : ApiClientConfiguration,
                      useValue: new ApiClientConfiguration(window.CS_API_URL, window.CS_AUTH_TOKEN, window.CS_AUTH_USER, window.CS_FILE_SERVER)
                  },
                  {
                      provide : AppEnvironmentConfiguration,
                      useValue: {
                          version  : environment.version,
                          theme    : environment.theme,
                          authToken: window.CS_AUTH_TOKEN,
                          authUser : window.CS_AUTH_USER
                      }
                  },
                  {
                      provide : AppReportConfiguration,
                      useValue: {
                          config: window.CS_REPORT_CONFIG
                      }
                  },
                  {
                      provide : APP_BASE_HREF,
                      useValue: "/"
                  },
                  AppService,
                  {
                      provide : ErrorHandler,
                      useClass: AppErrorService
                  },
                  {
                      provide : HTTP_INTERCEPTORS,
                      useClass: DedupeInterceptor,
                      multi   : true
                  },
                  {
                      provide : HTTP_INTERCEPTORS,
                      useClass: SourceTypeInterceptor,
                      multi   : true
                  },
                  {
                      provide : HTTP_INTERCEPTORS,
                      useClass: TraceHeadersInterceptor,
                      multi   : true
                  },
                  {
                      provide   : MAT_MENU_SCROLL_STRATEGY,
                      useFactory: (overlay: Overlay) => () =>
                          overlay.scrollStrategies.reposition(),
                      deps      : [Overlay]

                  },
                  {
                      provide   : FRAMEWORK_FEATURE_FLAGS,
                      useFactory: (ff: FeatureFlagsService,
                                   requester: FeatureFlagsRequester) => frameworkFeatureFlagsContextFactory(
                          ff.featureFlagsState,
                          (flagKey: FrameworkFeatureFlag) => requester.registerFeatureFlagRequest(Models.FeatureFlagCategory.FEATURE, flagKey)
                      ),
                      deps      : [
                          FeatureFlagsService,
                          FeatureFlagsRequester
                      ]
                  },
                  AppContext,
                  provideAppInitializer(() =>
                                        {
                                            const appService = inject(AppService);
                                            return appService.loadConfig(environment.theme);
                                        }),
                  provideHttpClient(withXhr(), withInterceptorsFromDi()),
                  provideAnimations(),
                  provideZoneChangeDetection({eventCoalescing: true})
              ]
          })
export class RootModule {}
